import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { Button } from '@pps/ui/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@pps/ui/components/ui/tooltip';
import { CircleHelp } from 'lucide-react';
import { TrainingVideosDialog } from './TrainingVideosDialog';
import { OpenTicketDialog } from './OpenTicketDialog';
import { capturarTela, type CapturaTela } from '@/lib/screenshot';

/**
 * Ícone de ajuda do header — acervo de vídeos de treinamento e abertura de
 * chamado de suporte. Fica ao lado dos ícones de WhatsApp/Instagram
 * (`SocialNotifications`).
 *
 * A CAPTURA DA TELA acontece aqui, no clique do ícone, e não lá no diálogo de
 * chamado. O motivo é simples: quando o diálogo está aberto ele cobre a tela, e
 * o print viraria uma foto do próprio formulário — inútil para quem vai
 * atender. Capturando antes, a imagem é da tela onde o problema apareceu.
 *
 * O custo é uma captura por clique no ícone, mesmo para quem só quer ver vídeo.
 * É assíncrona e não bloqueia a abertura do diálogo; a alternativa (capturar
 * depois, escondendo o overlay) depende de detalhes internos do Radix e quebra
 * silenciosamente quando eles mudam.
 */
export function HelpButton() {
  const { t } = useTranslation();
  const location = useLocation();
  const [aberto, setAberto] = useState(false);
  const [chamadoAberto, setChamadoAberto] = useState(false);
  const [captura, setCaptura] = useState<CapturaTela | null>(null);
  const rotaOrigem = useRef<string>(location.pathname);

  // object URL vaza se não for revogado — a imagem fica na memória do browser
  // até o reload.
  useEffect(() => {
    return () => {
      if (captura) URL.revokeObjectURL(captura.previewUrl);
    };
  }, [captura]);

  async function aoAbrir() {
    rotaOrigem.current = location.pathname;
    if (captura) URL.revokeObjectURL(captura.previewUrl);
    setCaptura(null);
    setAberto(true);
    // Sem await antes de abrir o diálogo: a espera pela captura não pode
    // atrasar o que o usuário pediu.
    const nova = await capturarTela();
    setCaptura(nova);
  }

  return (
    <>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="h-9 w-9"
            aria-label={t('help.title')}
            onClick={aoAbrir}
          >
            <CircleHelp className="h-[18px] w-[18px]" />
          </Button>
        </TooltipTrigger>
        <TooltipContent>{t('help.tooltip')}</TooltipContent>
      </Tooltip>

      <TrainingVideosDialog
        open={aberto}
        onOpenChange={setAberto}
        onReportarProblema={() => {
          setAberto(false);
          setChamadoAberto(true);
        }}
      />

      <OpenTicketDialog
        open={chamadoAberto}
        onOpenChange={setChamadoAberto}
        captura={captura}
        rotaOrigem={rotaOrigem.current}
      />
    </>
  );
}
